import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Container, Input, Button, Table } from "reactstrap";
import DashboardLayout from "./DashboardLayout";
import { useUser } from './Context/UserContext'; 

const VendorEvaluation = () => {
    const { user } = useUser();
    const currentUserRole = user?.roles?.[0]?.role?.trim().toUpperCase() || "";
    const currentUserEmpId = user?.roles?.[0]?.empId || "";
    const isDeptHead = currentUserRole.includes("DEPT") || currentUserRole.includes("HEAD");
    
    // ============================================================
    // STATE
    // ============================================================

    const [vendors, setVendors] = useState([]);
    const [criteria, setCriteria] = useState([]);
    const [evaluations, setEvaluations] = useState([]);

    const [selectedVendorId, setSelectedVendorId] = useState("");

    const emptySupplier = {
        companyName: "",
        address: "",
        contactPerson: "",
        rangeOfGoods: "",
        panRegistration: "",
        userId: ""
    };

    const [supplier, setSupplier] = useState(emptySupplier);
    const [ratings, setRatings] = useState({});

    const [loadingVendors, setLoadingVendors] = useState(false);
    const [loadingCriteria, setLoadingCriteria] = useState(false);
    const [loadingEvaluations, setLoadingEvaluations] = useState(false);
    const [saving, setSaving] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [historySearch, setHistorySearch] = useState("");

    const [ratingErrors, setRatingErrors] = useState({});

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [selectedHistoryId, setSelectedHistoryId] = useState("");
    const [isDark, setIsDark] = useState(
        () => document.body.classList.contains("dark-mode")
    );

    useEffect(() => {
        const handler = (e) => setIsDark(!!e.detail);
        window.addEventListener("vms:darkmode", handler);
        return () => window.removeEventListener("vms:darkmode", handler);
    }, []);
    // ============================================================
    // RATING OPTIONS
    // ============================================================

    const ratingOptions = [
        {
            value: "1",
            label: "1 - Poor"
        },
        {
            value: "2",
            label: "2 - Below Average"
        },
        {
            value: "3",
            label: "3 - Average"
        },
        {
            value: "4",
            label: "4 - Good"
        },
        {
            value: "5",
            label: "5 - Excellent"
        }
    ];

    // ============================================================
    // FORMAT DATE
    // ============================================================

    const formatDate = (value) => {
        if (!value) {
            return "-";
        }

        const date = new Date(value);

        if (isNaN(date.getTime())) {
            return value;
        }

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();

        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");

        return `${day} -${month} -${year} ${hours}:${minutes} `;
    };

    // ============================================================
    // GET CRITERIA ID
    // ============================================================

    const getCriteriaId = (item) => {
        return (
            item.id ??
            item.ID ??
            item.criteriaId ??
            item.CriteriaId
        );
    };

    // ============================================================
    // GET EVALUATION NAME
    // ============================================================

    const getEvaluationName = (item) => {
        return (
            item.evaluation ??
            item.Evaluation ??
            ""
        );
    };

    // ============================================================
    // GET CRITERIA DESCRIPTION
    // ============================================================

    const getCriteriaDescription = (item) => {
        return (
            item.criteria ??
            item.Criteria ??
            ""
        );
    };

    // ============================================================
    // CREATE EMPTY RATINGS
    // ============================================================

    const createEmptyRatings = (criteriaList = criteria) => {
        const emptyRatings = {};

        criteriaList.forEach((item) => {
            const id = getCriteriaId(item);

            if (
                id !== undefined &&
                id !== null
            ) {
                emptyRatings[id] = "";
            }
        });

        return emptyRatings;
    };

    // ============================================================
    // CALCULATE TOTAL RATING
    // ============================================================

    const getTotalRating = () => {
        return criteria.reduce((total, item) => {
            const criteriaId = getCriteriaId(item);
            const rating = Number(ratings[criteriaId]);

            if (!isNaN(rating) && rating > 0) {
                return total + rating;
            }

            return total;
        }, 0);
    };

    // ============================================================
    // CALCULATE AVERAGE RATING
    // ============================================================

    const getAverageRating = () => {
        if (!criteria || criteria.length === 0) {
            return 0;
        }

        const total = getTotalRating();

        return total / criteria.length;
    };

    // ============================================================
    // INITIAL LOAD
    // ============================================================

    useEffect(() => {
        setSelectedVendorId("");
        setSupplier(emptySupplier);
        setSelectedHistoryId("");

        loadVendors();
        loadCriteria();
        loadAllEvaluations();
    }, []);

    // ============================================================
    // LOAD VENDORS
    // ============================================================

    const loadVendors = async () => {
        try {
            setLoadingVendors(true);
            setError("");

            const response = await axios.get(
                `/api/vendordetails/vendor-list-evaluation?empId=${currentUserEmpId}`
            );
            const data = Array.isArray(response.data)
                ? response.data
                : [];

            setVendors(data);
        } catch (err) {
            console.error(
                "Error loading vendors:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Unable to load vendors."
            );
        } finally {
            setLoadingVendors(false);
        }
    };

    // ============================================================
    // LOAD CRITERIA
    // ============================================================

    const loadCriteria = async () => {
        try {
            setLoadingCriteria(true);
            setError("");

            const response = await axios.get(
                "/api/vendorevaluation/criteria"
            );

            const data = Array.isArray(response.data)
                ? response.data
                : [];

            setCriteria(data);

            setRatings(
                createEmptyRatings(data)
            );
        } catch (err) {
            console.error(
                "Error loading evaluation master:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Unable to load evaluation criteria."
            );
        } finally {
            setLoadingCriteria(false);
        }
    };

    // ============================================================
    // LOAD EVALUATIONS BASED ON USER ROLE
    // ============================================================

    const loadAllEvaluations = async () => {
        try {
            setLoadingEvaluations(true);

            let url = "/api/vendorevaluation/evaluations";

            // Finance → see ALL evaluation history
            if (currentUserRole.includes("FINANCE")) {
                url = "/api/vendorevaluation/evaluations";
            }
            // Department → see only their department history
            else {
                url = `/api/vendorevaluation/evaluations?empId=${encodeURIComponent(
                    currentUserEmpId
                )}`;
            }

            const response = await axios.get(url);

            const data = Array.isArray(response.data)
                ? response.data
                : [];

            console.log("Evaluation history:", data);
            console.log("Current role:", currentUserRole);
            console.log("Current empId:", currentUserEmpId);

            setEvaluations(data);

        } catch (err) {
            console.error(
                "Error loading evaluation history:",
                err
            );

            setEvaluations([]);

        } finally {
            setLoadingEvaluations(false);
        }
    };


    // ============================================================
    // GET VENDOR ID
    // ============================================================

    const getVendorId = (vendor) => {
        return (
            vendor?.id ??
            vendor?.ID ??
            vendor?.vendorId ??
            vendor?.VendorId
        );
    };

    // ============================================================
    // GET VENDOR COMPANY NAME
    // ============================================================

    const getVendorCompanyName = (vendor) => {
        return (
            vendor?.nameOfCompany ??
            vendor?.NameOfCompany ??
            vendor?.legalName ??
            vendor?.LegalName ??
            ""
        );
    };

    // ============================================================
    // GET VENDOR CODE
    // ============================================================

    const getVendorCode = (vendor) => {
        return (
            vendor?.vendorCode ??
            vendor?.VendorCode ??
            vendor?.vendorcode ??
            vendor?.Vendor_Code ??
            ""
        );
    };

    // ============================================================
    // GET COMPANY NAME WITH VENDOR CODE
    // ============================================================

    const getCompanyNameWithVendorCode = (
        vendorId,
        row = null
    ) => {
        const companyName =
            getCompanyNameByVendorId(
                vendorId,
                row
            );

        const vendor = vendors.find(
            (item) =>
                String(getVendorId(item)) ===
                String(vendorId)
        );

        const vendorCode = vendor
            ? getVendorCode(vendor)
            : (
                row?.vendorCode ??
                row?.VendorCode ??
                row?.vendorcode ??
                row?.Vendor_Code ??
                ""
            );

        if (!companyName || companyName === "-") {
            return "-";
        }

        return vendorCode
            ? `${companyName} (${vendorCode})`
            : companyName;
    };

    // ============================================================
    // GET VENDOR USER ID
    // ============================================================

    const getVendorUserId = (vendor) => {
        return (
            vendor?.userId ??
            vendor?.UserID ??
            vendor?.userid ??
            vendor?.UserId
        );
    };

    // ============================================================
    // GET HISTORY ID
    // ============================================================

    const getHistoryId = (row, index = 0) => {
        return (
            row?.id ??
            row?.ID ??
            row?.evaluationId ??
            row?.EvaluationId ??
            index + 1
        );
    };

    // ============================================================
    // GET HISTORY VENDOR ID
    // ============================================================

    const getHistoryVendorId = (row) => {
        return (
            row?.vendorId ??
            row?.VendorId ??
            row?.VendorID ??
            row?.vendorID ??
            row?.vendorid ??
            ""
        );
    };

    // ============================================================
    // GET HISTORY USER ID
    // ============================================================

    const getHistoryUserId = (row) => {
        return (
            row?.userId ??
            row?.UserId ??
            row?.UserID ??
            row?.userid ??
            row?.VendorUserId ??
            row?.vendorUserId ??
            ""
        );
    };

    // ============================================================
    // FIND COMPANY NAME FROM VENDOR ID
    // ============================================================

    const getCompanyNameByVendorId = (
        vendorId,
        row = null
    ) => {
        if (!vendorId) {
            return (
                row?.companyName ??
                row?.CompanyName ??
                "-"
            );
        }

        const rowCompany =
            row?.companyName ??
            row?.CompanyName ??
            row?.nameOfCompany ??
            row?.NameOfCompany ??
            "";

        if (rowCompany) {
            return rowCompany;
        }

        const vendor = vendors.find(
            (item) =>
                String(getVendorId(item)) ===
                String(vendorId)
        );

        if (vendor) {
            const companyName =
                getVendorCompanyName(vendor);

            if (companyName) {
                return companyName;
            }
        }

        return "-";
    };

    // ============================================================
    // LOAD VENDOR BY ID
    // ============================================================

    const loadVendorById = async (
        vendorId,
        vendorList = vendors
    ) => {
        if (!vendorId) {
            return;
        }

        setSelectedVendorId(
            String(vendorId)
        );

        setSuccess("");
        setError("");

        const selectedVendor =
            vendorList.find(
                (vendor) =>
                    String(
                        getVendorId(vendor)
                    ) === String(vendorId)
            );

        if (!selectedVendor) {
            return;
        }

        const vendorUserId =
            getVendorUserId(
                selectedVendor
            );

        // --------------------------------------------------------
        // LOAD BASIC VENDOR INFORMATION
        // --------------------------------------------------------

        setSupplier({
            companyName:
                selectedVendor.nameOfCompany ??
                selectedVendor.NameOfCompany ??
                selectedVendor.legalName ??
                selectedVendor.LegalName ??
                "",

            address:
                selectedVendor.address ??
                selectedVendor.Address ??
                selectedVendor.address1 ??
                selectedVendor.Address1 ??
                "",

            contactPerson:
                selectedVendor.contactPersonName ??
                selectedVendor.ContactPersonName ??
                selectedVendor.contactPerson ??
                selectedVendor.ContactPerson ??
                "",

            rangeOfGoods:
                selectedVendor.natureOfSupply ??
                selectedVendor.NatureOfSupply ??
                selectedVendor.productsSupplied ??
                selectedVendor.ProductsSupplied ??
                "",

            panRegistration:
                selectedVendor.pan ??
                selectedVendor.PAN ??
                selectedVendor.gst ??
                selectedVendor.GST ??
                selectedVendor.businessRegNo ??
                selectedVendor.BusinessRegNo ??
                "",

            userId: vendorUserId || ""
        });

        // --------------------------------------------------------
        // FETCH COMPLETE VENDOR DETAILS
        // --------------------------------------------------------

        if (vendorUserId) {
            try {
                const response = await axios.get(
                    `/ api / vendordetails / vendor - by - userid / ${vendorUserId} `
                );

                const data =
                    response.data || {};

                const address =
                    data.address ??
                    data.Address ??
                    [
                        data.address1 ??
                        data.Address1,

                        data.address2 ??
                        data.Address2,

                        data.city ??
                        data.City,

                        data.state ??
                        data.State,

                        data.country ??
                        data.Country,

                        data.postalCode ??
                        data.PostalCode
                    ]
                        .filter(Boolean)
                        .join(", ");

                const actualUserId =
                    data.userID ??
                    data.UserID ??
                    vendorUserId;

                setSupplier({
                    companyName:
                        data.nameOfCompany ??
                        data.NameOfCompany ??
                        data.legalName ??
                        data.LegalName ??
                        selectedVendor.nameOfCompany ??
                        "",

                    address: address,

                    contactPerson:
                        data.contactPersonName ??
                        data.ContactPersonName ??
                        data.contactPerson ??
                        data.ContactPerson ??
                        "",

                    rangeOfGoods:
                        data.natureOfSupply ??
                        data.NatureOfSupply ??
                        data.productsSupplied ??
                        data.ProductsSupplied ??
                        "",

                    panRegistration:
                        data.pan ??
                        data.PAN ??
                        data.gst ??
                        data.GST ??
                        data.businessRegNo ??
                        data.BusinessRegNo ??
                        "",

                    userId: actualUserId
                });
            } catch (err) {
                console.warn(
                    "Unable to fetch complete vendor details.",
                    err
                );
            }
        }
    };

    // ============================================================
    // COMPANY CHANGE
    // ============================================================

    const handleCompanyChange = async (e) => {
        const vendorId = e.target.value;

        setSelectedHistoryId("");
        setError("");
        setSuccess("");
        setRatingErrors({});

        if (!vendorId) {
            setSelectedVendorId("");
            setSupplier(emptySupplier);
            setRatings(createEmptyRatings());
            return;
        }

        await loadVendorById(
            vendorId,
            vendors
        );

        const savedEvaluation = evaluations.find(
            (row) => {
                const historyVendorId =
                    getHistoryVendorId(row);

                return (
                    String(historyVendorId) ===
                    String(vendorId)
                );
            }
        );

        if (!savedEvaluation) {
            setRatings(
                createEmptyRatings()
            );

            return;
        }

        const savedRatings =
            createEmptyRatings();

        criteria.forEach((item) => {
            const criteriaId =
                getCriteriaId(item);

            const evaluationName =
                getEvaluationName(item)
                    .toLowerCase()
                    .trim();

            let savedRating = "";

            if (
                evaluationName === "competency"
            ) {
                savedRating =
                    getHistoryRating(
                        savedEvaluation,
                        "competency"
                    );
            } else if (
                evaluationName === "capability"
            ) {
                savedRating =
                    getHistoryRating(
                        savedEvaluation,
                        "capability"
                    );
            } else if (
                evaluationName.includes("quality")
            ) {
                savedRating =
                    getHistoryRating(
                        savedEvaluation,
                        "qualitySystem"
                    );
            } else if (
                evaluationName.includes("timeliness")
            ) {
                savedRating =
                    getHistoryRating(
                        savedEvaluation,
                        "timelinessOfDelivery"
                    );
            } else if (
                evaluationName.includes("competitiveness") ||
                evaluationName.includes("price")
            ) {
                savedRating =
                    getHistoryRating(
                        savedEvaluation,
                        "competitivenessOnPrice"
                    );
            } else if (
                evaluationName.includes("terms") ||
                evaluationName.includes("condition")
            ) {
                savedRating =
                    getHistoryRating(
                        savedEvaluation,
                        "termsConditions"
                    );
            }

            savedRatings[criteriaId] =
                savedRating !== null &&
                    savedRating !== undefined
                    ? String(savedRating)
                    : "";
        });

        setRatings(savedRatings);

        setSelectedHistoryId(
            String(
                getHistoryId(
                    savedEvaluation
                )
            )
        );
    };

    // ============================================================
    // RATING CHANGE
    // ============================================================

    const handleRatingChange = (
        criteriaId,
        value
    ) => {
        setRatings((previous) => ({
            ...previous,
            [criteriaId]: value
        }));

        setRatingErrors((previous) => {
            const updated = {
                ...previous
            };

            delete updated[criteriaId];

            return updated;
        });

        setError("");
        setSuccess("");
    };

    // ============================================================
    // CLEAR COMPLETE FORM
    // ============================================================

    const clearCompleteForm = () => {
        setSelectedVendorId("");

        setSupplier({
            ...emptySupplier
        });

        setRatings(
            createEmptyRatings()
        );

        setSelectedHistoryId("");
        setRatingErrors({});
    };

    // ============================================================
    // CLEAR BUTTON
    // ============================================================

    const handleClear = () => {
        clearCompleteForm();

        setError("");
        setSuccess("");
    };

    // ============================================================
    // VALIDATE FORM
    // ============================================================

    const validateForm = () => {
        if (!selectedVendorId) {
            setError(
                "Please select a Company Name before saving."
            );

            return false;
        }

        if (!supplier.userId) {
            setError(
                "Vendor User ID was not found. Please select the company again."
            );

            return false;
        }

        if (
            !criteria ||
            criteria.length === 0
        ) {
            setError(
                "No evaluation criteria found."
            );

            return false;
        }

        const newRatingErrors = {};

        for (const item of criteria) {
            const criteriaId =
                getCriteriaId(item);

            const rating =
                ratings[criteriaId];

            if (
                rating === undefined ||
                rating === null ||
                rating === ""
            ) {
                newRatingErrors[criteriaId] =
                    `Please select rating for ${getEvaluationName(item)}.`;
            }
        }

        if (
            Object.keys(newRatingErrors).length > 0
        ) {
            setRatingErrors(
                newRatingErrors
            );

            setError(
                "Please select a rating for all evaluation criteria."
            );

            return false;
        }

        setRatingErrors({});
        setError("");

        return true;
    };

    // ============================================================
    // SAVE
    // ============================================================

    const handleSave = async () => {
        setError("");
        setSuccess("");

        if (!validateForm()) {
            return;
        }

        const userId =
            Number(supplier.userId);

        const vendorId =
            Number(selectedVendorId);

        if (!userId || userId <= 0) {
            setError(
                "Invalid vendor User ID."
            );

            return;
        }

        if (!vendorId || vendorId <= 0) {
            setError(
                "Invalid Vendor ID."
            );

            return;
        }

        const payload = {
            VendorId: vendorId,

            UserId: userId,

            VendorUserId: userId,

            CompanyName:
                supplier.companyName,

            EmpId: currentUserEmpId,           

            Evaluations:
                criteria.map((item) => {
                    const criteriaId =
                        getCriteriaId(item);

                    return {
                        CriteriaId:
                            Number(criteriaId),

                        Evaluation:
                            getEvaluationName(item),

                        Rating:
                            Number(
                                ratings[criteriaId]
                            )
                    };
                })
        };

        console.log(
            "Vendor Evaluation Payload:",
            payload
        );

        try {
            setSaving(true);

            const response =
                await axios.post(
                    "/api/vendorevaluation/save",
                    payload
                );

            console.log(
                "Save response:",
                response.data
            );

            setSuccess(
                response.data?.message ||
                "Vendor evaluation saved successfully."
            );

            await loadAllEvaluations();

            clearCompleteForm();

        } catch (err) {
            console.error(
                "Error saving vendor evaluation:",
                err
            );

            console.error(
                "Save error response:",
                err.response?.data
            );

            setError(
                err.response?.data?.message ||
                err.response?.data?.error ||
                "Unable to save vendor evaluation."
            );
        } finally {
            setSaving(false);
        }
    };

    // ============================================================
    // GET RATING LABEL
    // ============================================================

    const getRatingLabel = (rating) => {
        const found =
            ratingOptions.find(
                (item) =>
                    String(item.value) ===
                    String(rating)
            );

        return found
            ? found.label
            : "-";
    };

    // ============================================================
    // GET RATING FROM HISTORY
    // ============================================================

    const getHistoryRating = (
        row,
        type
    ) => {
        switch (type) {
            case "competency":
                return (
                    row.competency ??
                    row.Competency ??
                    ""
                );

            case "capability":
                return (
                    row.capability ??
                    row.Capability ??
                    ""
                );

            case "qualitySystem":
                return (
                    row.qualitySystem ??
                    row.QualitySystem ??
                    ""
                );

            case "timelinessOfDelivery":
                return (
                    row.timelinessOfDelivery ??
                    row.TimelinessOfDelivery ??
                    ""
                );

            case "competitivenessOnPrice":
                return (
                    row.competitivenessOnPrice ??
                    row.CompetitivenessOnPrice ??
                    ""
                );

            case "termsConditions":
                return (
                    row.termsConditions ??
                    row.TermsConditions ??
                    ""
                );

            default:
                return "";
        }
    };

    // ============================================================
    // LOAD SAVED HISTORY INTO FORM
    // ============================================================

    const handleHistoryRowClick = async (
        row,
        index
    ) => {
        const historyId =
            getHistoryId(
                row,
                index
            );

        const vendorId =
            getHistoryVendorId(row);

        const userId =
            getHistoryUserId(row);

        setSelectedHistoryId(
            String(historyId)
        );

        setError("");
        setSuccess("");
        setRatingErrors({});

        if (!vendorId) {
            setError(
                "Vendor ID was not found in this saved evaluation."
            );

            return;
        }

        const selectedVendor =
            vendors.find(
                (vendor) =>
                    String(
                        getVendorId(vendor)
                    ) ===
                    String(vendorId)
            );

        if (selectedVendor) {
            setSelectedVendorId(
                String(vendorId)
            );

            await loadVendorById(
                vendorId,
                vendors
            );
        } else {
            setSelectedVendorId(
                String(vendorId)
            );

            setSupplier({
                companyName:
                    row.companyName ??
                    row.CompanyName ??
                    getCompanyNameByVendorId(
                        vendorId,
                        row
                    ),

                address:
                    row.address ??
                    row.Address ??
                    "",

                contactPerson:
                    row.contactPerson ??
                    row.ContactPerson ??
                    "",

                rangeOfGoods:
                    row.rangeOfGoods ??
                    row.RangeOfGoods ??
                    "",

                panRegistration:
                    row.panRegistration ??
                    row.PAN ??
                    row.pan ??
                    "",

                userId:
                    userId || ""
            });
        }

        const savedRatings =
            createEmptyRatings();

        criteria.forEach((item) => {
            const criteriaId =
                getCriteriaId(item);

            const evaluationName =
                getEvaluationName(item)
                    .toLowerCase()
                    .trim();

            let savedRating = "";

            if (
                evaluationName ===
                "competency"
            ) {
                savedRating =
                    getHistoryRating(
                        row,
                        "competency"
                    );
            } else if (
                evaluationName ===
                "capability"
            ) {
                savedRating =
                    getHistoryRating(
                        row,
                        "capability"
                    );
            } else if (
                evaluationName.includes(
                    "quality"
                )
            ) {
                savedRating =
                    getHistoryRating(
                        row,
                        "qualitySystem"
                    );
            } else if (
                evaluationName.includes(
                    "timeliness"
                )
            ) {
                savedRating =
                    getHistoryRating(
                        row,
                        "timelinessOfDelivery"
                    );
            } else if (
                evaluationName.includes(
                    "competitiveness"
                ) ||
                evaluationName.includes(
                    "price"
                )
            ) {
                savedRating =
                    getHistoryRating(
                        row,
                        "competitivenessOnPrice"
                    );
            } else if (
                evaluationName.includes(
                    "terms"
                ) ||
                evaluationName.includes(
                    "condition"
                )
            ) {
                savedRating =
                    getHistoryRating(
                        row,
                        "termsConditions"
                    );
            }

            savedRatings[
                criteriaId
            ] =
                savedRating !== null &&
                    savedRating !== undefined
                    ? String(savedRating)
                    : "";
        });

        setRatings(savedRatings);
    };

    // ============================================================
    // SEARCH + PAGINATION
    // ============================================================

    const filteredEvaluations =
        evaluations.filter((row) => {
            const vendorId =
                getHistoryVendorId(row);

            const companyName =
                getCompanyNameByVendorId(
                    vendorId,
                    row
                );

            const vendor =
                vendors.find(
                    (item) =>
                        String(
                            getVendorId(item)
                        ) ===
                        String(vendorId)
                );

            const vendorCode =
                vendor
                    ? getVendorCode(vendor)
                    : (
                        row?.vendorCode ??
                        row?.VendorCode ??
                        row?.vendorcode ??
                        row?.Vendor_Code ??
                        ""
                    );

            const searchText =
                `${companyName} ${vendorCode} `
                    .toLowerCase();

            return searchText.includes(
                historySearch
                    .toLowerCase()
                    .trim()
            );
        });

    const totalPages = Math.max(
        1,
        Math.ceil(
            filteredEvaluations.length /
            itemsPerPage
        )
    );

    const startIndex =
        (currentPage - 1) *
        itemsPerPage;

    const currentEvaluations =
        filteredEvaluations.slice(
            startIndex,
            startIndex + itemsPerPage
        );

    // ============================================================
    // RENDER
    // ============================================================
    const styles = useMemo(() => getStyles(isDark), [isDark]);

    return (
        <DashboardLayout>

            <div style={styles.pageWrapper}>

                <Container
                    fluid
                    style={styles.container}
                >

                    {/* ==================================================
                        PAGE HEADER
                    ================================================== */}

                    <div style={styles.pageHeader}>

                        <h2 style={styles.pageTitle}>
                            Vendor Evaluation
                        </h2>

                        <p style={styles.pageSubtitle}>
                            Evaluate and record vendor performance
                        </p>

                    </div>

                    {/* ==================================================
                        ERROR
                    ================================================== */}

                    {error && (
                        <div style={styles.errorBox}>
                            {error}
                        </div>
                    )}

                    {/* ==================================================
                        SUCCESS
                    ================================================== */}

                    {success && (
                        <div style={styles.successBox}>
                            {success}
                        </div>
                    )}

                    {/* ==================================================
                        1. SUPPLIER INFORMATION
                    ================================================== */}

                    <div style={styles.formBox}>

                        <div style={styles.boxHeader}>

                            <h4 style={styles.boxTitle}>
                                Supplier Information
                            </h4>

                            <div style={styles.headerLine} />

                            <p style={styles.boxSubtitle}>
                                Select a vendor to automatically load supplier details
                            </p>

                        </div>

                        <div style={styles.formBody}>

                            {/* COMPANY */}

                            <div style={styles.fullField}>

                                <label style={styles.label}>
                                    Company Name
                                    <span style={styles.required}>
                                        {" "}*
                                    </span>
                                </label>

                                <Input
                                    type="select"
                                    value={selectedVendorId}
                                    onChange={handleCompanyChange}
                                    disabled={loadingVendors}
                                    style={styles.select}
                                >

                                    <option value="">
                                        {loadingVendors
                                            ? "Loading companies..."
                                            : "Select Company"}
                                    </option>

                                    {vendors.map(
                                        (
                                            vendor,
                                            index
                                        ) => {

                                            const id =
                                                getVendorId(
                                                    vendor
                                                );

                                            const company =
                                                getVendorCompanyName(
                                                    vendor
                                                );

                                            const vendorCode =
                                                getVendorCode(
                                                    vendor
                                                );

                                            return (
                                                <option
                                                    key={
                                                        id ??
                                                        index
                                                    }
                                                    value={id}
                                                >
                                                    {
                                                        company ||
                                                        "Unnamed Vendor"
                                                    }
                                                    {
                                                        vendorCode
                                                            ? ` (${vendorCode})`
                                                            : ""
                                                    }
                                                </option>
                                            );
                                        }
                                    )}

                                </Input>

                            </div>

                            {/* SUPPLIER DETAILS */}

                            <div style={styles.supplierGrid}>

                                <div>

                                    <label style={styles.label}>
                                        Address
                                    </label>

                                    <Input
                                        type="textarea"
                                        value={supplier.address}
                                        readOnly
                                        placeholder="Automatically populated"
                                        style={styles.textarea}
                                    />

                                </div>

                                <div>

                                    <label style={styles.label}>
                                        Contact Person
                                    </label>

                                    <Input
                                        type="text"
                                        value={supplier.contactPerson}
                                        readOnly
                                        placeholder="Automatically populated"
                                        style={styles.input}
                                    />

                                </div>

                                <div>

                                    <label style={styles.label}>
                                        Range of Goods / Services Offered
                                    </label>

                                    <Input
                                        type="textarea"
                                        value={supplier.rangeOfGoods}
                                        readOnly
                                        placeholder="Automatically populated"
                                        style={styles.textarea}
                                    />

                                </div>

                                <div>

                                    <label style={styles.label}>
                                        PAN / TIN / Registration No.
                                    </label>

                                    <Input
                                        type="text"
                                        value={supplier.panRegistration}
                                        readOnly
                                        placeholder="Automatically populated"
                                        style={styles.input}
                                    />

                                </div>

                            </div>

                        </div>

                    </div>

                    {/* ==================================================
                        2. EVALUATION CRITERIA
                    ================================================== */}

                    <div style={styles.formBox}>

                        <div style={styles.boxHeader}>

                            <h4 style={styles.boxTitle}>
                                Evaluation Criteria
                            </h4>

                            <div style={styles.headerLine} />

                            <p style={styles.boxSubtitle}>
                                Rate the supplier based on the evaluation criteria
                            </p>

                        </div>

                        <div style={styles.formBody}>

                            {loadingCriteria ? (

                                <div style={styles.loadingText}>
                                    Loading evaluation criteria...
                                </div>

                            ) : criteria.length === 0 ? (

                                <div style={styles.emptyText}>
                                    No evaluation criteria available.
                                </div>

                            ) : (

                                <Table
                                    bordered
                                    style={styles.criteriaTable}
                                >

                                    <thead>

                                        <tr>

                                            <th style={styles.thNumber}>
                                                ID
                                            </th>

                                            <th style={styles.thEvaluation}>
                                                Evaluation
                                            </th>

                                            <th style={styles.thCriteria}>
                                                Criteria
                                            </th>

                                            <th style={styles.thRating}>
                                                Rating
                                            </th>

                                        </tr>

                                    </thead>

                                    <tbody>

                                        {criteria.map(
                                            (
                                                item,
                                                index
                                            ) => {

                                                const criteriaId =
                                                    getCriteriaId(
                                                        item
                                                    );

                                                return (
                                                    <tr
                                                        key={
                                                            criteriaId ??
                                                            index
                                                        }
                                                    >

                                                        <td style={styles.tdNumber}>
                                                            {
                                                                criteriaId ??
                                                                index + 1
                                                            }
                                                        </td>

                                                        <td style={styles.evaluationCell}>
                                                            {
                                                                getEvaluationName(
                                                                    item
                                                                )
                                                            }
                                                        </td>

                                                        <td style={styles.criteriaCell}>
                                                            {
                                                                getCriteriaDescription(
                                                                    item
                                                                )
                                                            }
                                                        </td>

                                                        <td style={styles.ratingCellInput}>

                                                            <Input
                                                                type="select"
                                                                value={
                                                                    ratings[
                                                                    criteriaId
                                                                    ] || ""
                                                                }
                                                                onChange={(
                                                                    e
                                                                ) =>
                                                                    handleRatingChange(
                                                                        criteriaId,
                                                                        e.target.value
                                                                    )
                                                                }
                                                                style={{
                                                                    ...styles.ratingSelect,
                                                                    ...(ratingErrors[
                                                                        criteriaId
                                                                    ]
                                                                        ? {
                                                                            border:
                                                                                "1px solid #dc3545",
                                                                            background:
                                                                                "#fff8f8"
                                                                        }
                                                                        : {})
                                                                }}
                                                            >

                                                                <option value="">
                                                                    Select Rating
                                                                </option>

                                                                {ratingOptions.map(
                                                                    (
                                                                        option
                                                                    ) => (
                                                                        <option
                                                                            key={
                                                                                option.value
                                                                            }
                                                                            value={
                                                                                option.value
                                                                            }
                                                                        >
                                                                            {
                                                                                option.label
                                                                            }
                                                                        </option>
                                                                    )
                                                                )}

                                                            </Input>

                                                            {ratingErrors[
                                                                criteriaId
                                                            ] && (
                                                                    <div
                                                                        data-rating-error="true"
                                                                        style={styles.ratingError}
                                                                    >
                                                                        {
                                                                            ratingErrors[
                                                                            criteriaId
                                                                            ]
                                                                        }
                                                                    </div>
                                                                )}

                                                        </td>

                                                    </tr>
                                                );
                                            }
                                        )}

                                        {/* ==================================================
                                            TOTAL + AVERAGE INSIDE TABLE
                                        ================================================== */}

                                        <tr>

                                            <td
                                                colSpan="4"
                                                style={styles.ratingSummaryCell}
                                            >

                                                <span style={styles.totalRatingText}>
                                                    Total Rating:
                                                    {" "}
                                                    {getTotalRating()}
                                                </span>

                                                <span style={styles.averageRatingText}>
                                                    Average Rating:
                                                    {" "}
                                                    {getAverageRating().toFixed(2)}
                                                </span>

                                            </td>

                                        </tr>

                                    </tbody>

                                </Table>

                            )}

                        </div>

                    </div>

                    {/* ==================================================
                        SAVE / CLEAR
                    ================================================== */}

                    <div style={styles.actionArea}>

                        <Button
                            style={styles.saveButton}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving
                                ? "Saving..."
                                : "Save"}
                        </Button>

                        <Button
                            style={styles.clearButton}
                            onClick={handleClear}
                            disabled={saving}
                        >
                            Clear
                        </Button>

                    </div>

                    {/* ==================================================
                        SEARCH + PAGINATION ABOVE 3RD BOX
                    ================================================== */}

                    <div style={styles.historyTopBar}>

                        {/* LEFT - SEARCH */}

                        <Input
                            type="text"
                            placeholder="Search by Company"
                            value={historySearch}
                            onChange={(e) => {
                                setHistorySearch(
                                    e.target.value
                                );
                                setCurrentPage(1);
                            }}
                            style={styles.historySearch}
                        />

                        {/* RIGHT - PAGINATION */}

                        <div style={styles.historyPagination}>

                            <Button
                                style={styles.pageButton}
                                disabled={
                                    currentPage === 1
                                }
                                onClick={() =>
                                    setCurrentPage(
                                        (prev) =>
                                            prev - 1
                                    )
                                }
                            >
                                Previous
                            </Button>

                            <span style={styles.pageNumber}>
                                Page {currentPage} of {totalPages}
                            </span>

                            <Button
                                style={styles.pageButton}
                                disabled={
                                    currentPage ===
                                    totalPages
                                }
                                onClick={() =>
                                    setCurrentPage(
                                        (prev) =>
                                            prev + 1
                                    )
                                }
                            >
                                Next
                            </Button>

                        </div>

                    </div>

                    {/* ==================================================
                        3. VENDOR EVALUATION HISTORY
                    ================================================== */}

                    <div style={styles.formBox}>

                        <div style={styles.boxHeader}>

                            <h4 style={styles.boxTitle}>
                                Vendor Evaluation History
                            </h4>

                            <div style={styles.headerLine} />

                            <p style={styles.boxSubtitle}>
                                Click a saved evaluation to load it back into the form
                            </p>

                        </div>

                        <div style={styles.formBody}>

                            {loadingEvaluations ? (

                                <div style={styles.loadingText}>
                                    Loading evaluation history...
                                </div>

                            ) : evaluations.length === 0 ? (

                                <div style={styles.emptyText}>
                                    No vendor evaluations found.
                                </div>

                            ) : (

                                <div style={styles.historyTableContainer}>

                                    <Table
                                        bordered
                                        hover
                                        style={styles.evaluationTable}
                                    >

                                        <thead>

                                            <tr>

                                                <th style={styles.thCompany}>
                                                    Company
                                                </th>

                                                <th style={styles.thDynamic}>
                                                    Competency
                                                </th>

                                                <th style={styles.thDynamic}>
                                                    Capability
                                                </th>

                                                <th style={styles.thDynamic}>
                                                    Quality System
                                                </th>

                                                <th style={styles.thDynamic}>
                                                    Timeliness of Delivery
                                                </th>

                                                <th style={styles.thDynamic}>
                                                    Competitiveness on Price
                                                </th>

                                                <th style={styles.thDynamic}>
                                                    Terms & Conditions
                                                </th>

                                                <th style={styles.thCreated}>
                                                    Created Date
                                                </th>

                                            </tr>

                                        </thead>

                                        <tbody>

                                            {currentEvaluations.map(
                                                (
                                                    row,
                                                    index
                                                ) => {

                                                    const evaluationId =
                                                        getHistoryId(
                                                            row,
                                                            startIndex +
                                                            index
                                                        );

                                                    const vendorId =
                                                        getHistoryVendorId(
                                                            row
                                                        );

                                                    const companyName =
                                                        getCompanyNameWithVendorCode(
                                                            vendorId,
                                                            row
                                                        );

                                                    const isSelected =
                                                        String(
                                                            selectedHistoryId
                                                        ) ===
                                                        String(
                                                            evaluationId
                                                        );

                                                    return (

                                                        <tr
                                                            key={
                                                                evaluationId
                                                            }
                                                            onClick={() =>
                                                                handleHistoryRowClick(
                                                                    row,
                                                                    startIndex +
                                                                    index
                                                                )
                                                            }
                                                            style={{
                                                                ...styles.historyRow,
                                                                ...(isSelected
                                                                    ? styles.selectedHistoryRow
                                                                    : {})
                                                            }}
                                                        >

                                                            <td style={styles.companyCell}>
                                                                {
                                                                    companyName
                                                                }
                                                            </td>

                                                            <td style={styles.ratingCell}>
                                                                {
                                                                    getRatingLabel(
                                                                        getHistoryRating(
                                                                            row,
                                                                            "competency"
                                                                        )
                                                                    )
                                                                }
                                                            </td>

                                                            <td style={styles.ratingCell}>
                                                                {
                                                                    getRatingLabel(
                                                                        getHistoryRating(
                                                                            row,
                                                                            "capability"
                                                                        )
                                                                    )
                                                                }
                                                            </td>

                                                            <td style={styles.ratingCell}>
                                                                {
                                                                    getRatingLabel(
                                                                        getHistoryRating(
                                                                            row,
                                                                            "qualitySystem"
                                                                        )
                                                                    )
                                                                }
                                                            </td>

                                                            <td style={styles.ratingCell}>
                                                                {
                                                                    getRatingLabel(
                                                                        getHistoryRating(
                                                                            row,
                                                                            "timelinessOfDelivery"
                                                                        )
                                                                    )
                                                                }
                                                            </td>

                                                            <td style={styles.ratingCell}>
                                                                {
                                                                    getRatingLabel(
                                                                        getHistoryRating(
                                                                            row,
                                                                            "competitivenessOnPrice"
                                                                        )
                                                                    )
                                                                }
                                                            </td>

                                                            <td style={styles.ratingCell}>
                                                                {
                                                                    getRatingLabel(
                                                                        getHistoryRating(
                                                                            row,
                                                                            "termsConditions"
                                                                        )
                                                                    )
                                                                }
                                                            </td>

                                                            <td style={styles.dateCell}>
                                                                {formatDate(
                                                                    row.createdDate ??
                                                                    row.CreatedDate
                                                                )}
                                                            </td>

                                                        </tr>

                                                    );
                                                }
                                            )}

                                        </tbody>

                                    </Table>

                                </div>

                            )}

                        </div>

                    </div>

                </Container>

            </div>

        </DashboardLayout>
    );
}; 

const getStyles = (isDark) => ({

    // ============================================================
    // PAGE
    // ============================================================

    pageWrapper: {
        minHeight: "100vh",
        background: isDark ? "#0b0f19" : "#f4f6f8",
        padding: "18px 20px 35px 20px"
    },

    container: {
        maxWidth: "1300px",
        margin: "0 auto"
    },

    pageHeader: {
        textAlign: "center",
        marginBottom: "18px"
    },

    pageTitle: {
        margin: 0,
        fontSize: "28px",
        fontWeight: "600",
        color: isDark ? "#e6ebf0" : "#173f35",
        letterSpacing: "0.3px"
    },

    pageSubtitle: {
        marginTop: "5px",
        marginBottom: 0,
        color: isDark ? "#9aa7b4" : "#7a858c",
        fontSize: "14px"
    },

    // ============================================================
    // MESSAGE
    // ============================================================

    errorBox: {
        background: isDark ? "#3a1f1f" : "#fce4e4",
        border: isDark ? "1px solid #6b3232" : "1px solid #f3bcbc",
        color: isDark ? "#f5a3a3" : "#9b2c2c",
        padding: "10px 14px",
        borderRadius: "5px",
        marginBottom: "14px",
        fontSize: "14px"
    },

    successBox: {
        background: isDark ? "#183a26" : "#e5f5e9",
        border: isDark ? "1px solid #2f6b45" : "1px solid #b8dfc0",
        color: isDark ? "#8fe0a8" : "#256b37",
        padding: "10px 14px",
        borderRadius: "5px",
        marginBottom: "14px",
        fontSize: "14px"
    },

    ratingError: {
        color: "#dc3545",
        fontSize: "11px",
        fontWeight: "500",
        marginTop: "4px",
        textAlign: "left",
        lineHeight: "1.3"
    },

    // ============================================================
    // BOX
    // ============================================================

    formBox: {
        background: isDark ? "#0f1b2e" : "#ffffff",
        border: isDark ? "1px solid #24344a" : "1px solid #cfcfcf",
        borderRadius: "7px",
        marginBottom: "16px",
        boxShadow: isDark
            ? "0 2px 7px rgba(0,0,0,0.4)"
            : "0 2px 7px rgba(0,0,0,0.07)",
        overflow: "hidden"
    },

    boxHeader: {
        padding: "11px 20px 8px 20px",
        background: isDark ? "#0f1b2e" : "#ffffff"
    },

    boxTitle: {
        textAlign: "center",
        margin: 0,
        color: isDark ? "#e6ebf0" : "#173f35",
        fontSize: "19px",
        fontWeight: "600"
    },

    headerLine: {
        height: "2px",
        background: isDark ? "#2f8a6f" : "#1d5c4d",
        marginTop: "6px",
        marginBottom: "5px"
    },

    boxSubtitle: {
        textAlign: "center",
        margin: 0,
        color: isDark ? "#8b96a1" : "#899298",
        fontSize: "12px"
    },

    formBody: {
        borderTop: isDark ? "1px solid #24344a" : "1px solid #e1e1e1",
        padding: "14px 20px"
    },

    // ============================================================
    // SUPPLIER FORM
    // ============================================================

    fullField: {
        marginBottom: "13px"
    },

    supplierGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "12px 16px"
    },

    label: {
        display: "block",
        marginBottom: "5px",
        color: isDark ? "#c3ccd4" : "#40515a",
        fontSize: "13px",
        fontWeight: "600"
    },

    required: {
        color: "#e05353"
    },

    input: {
        height: "38px",
        border: isDark ? "1px solid #2c3e54" : "1px solid #cfd5d8",
        borderRadius: "5px",
        fontSize: "13px",
        background: isDark ? "#132238" : "#f8f9fa",
        color: isDark ? "#c3ccd4" : "#5c666c"
    },

    select: {
        height: "40px",
        border: isDark ? "1px solid #2c3e54" : "1px solid #c8ced1",
        borderRadius: "5px",
        fontSize: "13px",
        background: isDark ? "#132238" : "#ffffff",
        color: isDark ? "#e6ebf0" : "#1a1a1a"
    },

    textarea: {
        minHeight: "58px",
        height: "58px",
        border: isDark ? "1px solid #2c3e54" : "1px solid #cfd5d8",
        borderRadius: "5px",
        fontSize: "13px",
        background: isDark ? "#132238" : "#f8f9fa",
        color: isDark ? "#c3ccd4" : "#5c666c",
        resize: "vertical"
    },

    // ============================================================
    // CRITERIA TABLE
    // ============================================================

    criteriaTable: {
        marginBottom: 0,
        width: "100%",
        fontSize: "13px",
        verticalAlign: "middle",
        color: isDark ? "#dbe2e8" : "#212529"
    },

    thNumber: {
        width: "50px",
        textAlign: "center",
        background: isDark ? "#132238" : "#eef3f1",
        color: isDark ? "#cfe3da" : "#24483e",
        fontWeight: "600",
        padding: "8px 4px",
        whiteSpace: "nowrap"
    },

    thEvaluation: {
        width: "21%",
        background: isDark ? "#132238" : "#eef3f1",
        color: isDark ? "#cfe3da" : "#24483e",
        fontWeight: "600",
        padding: "10px 12px",
        textAlign: "left"
    },

    thCriteria: {
        width: "40%",
        background: isDark ? "#132238" : "#eef3f1",
        color: isDark ? "#cfe3da" : "#24483e",
        fontWeight: "600",
        padding: "10px 12px",
        textAlign: "left"
    },

    thRating: {
        width: "150px",
        textAlign: "center",
        background: isDark ? "#132238" : "#eef3f1",
        color: isDark ? "#cfe3da" : "#24483e",
        fontWeight: "600",
        padding: "10px"
    },

    // ============================================================
    // RATING INPUT
    // ============================================================

    ratingCellInput: {
        width: "150px",
        verticalAlign: "middle",
        padding: "8px",
        textAlign: "center"
    },

    ratingSelect: {
        height: "34px",
        width: "120px",
        borderRadius: "5px",
        fontSize: "12px",
        background: isDark ? "#132238" : "#ffffff",
        color: isDark ? "#e6ebf0" : "#1a1a1a",
        margin: "0 auto"
    },

    // ============================================================
    // TOTAL + AVERAGE
    // ============================================================

    ratingSummaryCell: {
        textAlign: "right",
        padding: "10px 14px",
        background: isDark ? "#0c1522" : "#f7faf9",
        borderTop: isDark ? "2px solid #2f8a6f" : "2px solid #1d5c4d",
        color: isDark ? "#e6ebf0" : "#173f35",
        fontSize: "13px"
    },

    totalRatingText: {
        fontWeight: "700",
        marginRight: "35px"
    },

    averageRatingText: {
        fontWeight: "700"
    },

    // ============================================================
    // HISTORY TABLE
    // ============================================================

    historyTableContainer: {
        width: "100%",
        overflowX: "visible",
        overflowY: "visible"
    },

    evaluationTable: {
        marginBottom: 0,
        width: "100%",
        minWidth: "0",
        tableLayout: "fixed",
        fontSize: "11px",
        verticalAlign: "middle",
        color: isDark ? "#dbe2e8" : "#212529"
    },

    // ============================================================
    // SEARCH + PAGINATION
    // ============================================================

    historyTopBar: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        marginBottom: "8px"
    },

    historySearch: {
        width: "260px",
        height: "36px",
        border: isDark ? "1px solid #2c3e54" : "1px solid #c8ced1",
        borderRadius: "5px",
        fontSize: "13px",
        padding: "6px 10px",
        background: isDark ? "#132238" : "#ffffff",
        color: isDark ? "#e6ebf0" : "#1a1a1a"
    },

    historyPagination: {
        display: "flex",
        alignItems: "center",
        gap: "10px"
    },

    // ============================================================
    // HISTORY HEADERS
    // ============================================================

    thCompany: {
        width: "85px",
        textAlign: "center",
        background: isDark ? "#132238" : "#eef3f1",
        color: isDark ? "#cfe3da" : "#24483e",
        fontWeight: "600",
        padding: "6px 2px",
        whiteSpace: "normal",
        wordBreak: "break-word"
    },

    thDynamic: {
        width: "calc((100% - 190px) / 6)",
        textAlign: "center",
        background: isDark ? "#132238" : "#eef3f1",
        color: isDark ? "#cfe3da" : "#24483e",
        fontWeight: "600",
        padding: "6px 3px",
        wordBreak: "break-word",
        whiteSpace: "normal"
    },

    thCreated: {
        width: "105px",
        textAlign: "center",
        background: isDark ? "#132238" : "#eef3f1",
        color: isDark ? "#cfe3da" : "#24483e",
        fontWeight: "600",
        padding: "6px 3px",
        wordBreak: "break-word",
        whiteSpace: "normal"
    },

    // ============================================================
    // TABLE CELLS
    // ============================================================

    tdNumber: {
        textAlign: "center",
        verticalAlign: "middle",
        fontWeight: "500",
        padding: "8px 3px",
        whiteSpace: "nowrap"
    },

    evaluationCell: {
        verticalAlign: "middle",
        fontWeight: "500",
        color: isDark ? "#dbe2e8" : "#35434a",
        padding: "10px 12px"
    },

    criteriaCell: {
        verticalAlign: "middle",
        color: isDark ? "#a9b4bd" : "#35434a",
        padding: "10px 12px",
        lineHeight: "1.4"
    },

    companyCell: {
        textAlign: "center",
        verticalAlign: "middle",
        padding: "6px 2px",
        fontSize: "11px",
        fontWeight: "500",
        color: isDark ? "#dbe2e8" : "#35434a",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        lineHeight: "1.25"
    },

    ratingCell: {
        textAlign: "center",
        verticalAlign: "middle",
        fontSize: "11px",
        fontWeight: "500",
        padding: "8px 2px",
        color: isDark ? "#dbe2e8" : "#212529",
        wordBreak: "break-word",
        whiteSpace: "normal",
        lineHeight: "1.25"
    },

    dateCell: {
        textAlign: "center",
        verticalAlign: "middle",
        fontSize: "12px",
        fontWeight: "500",
        padding: "6px 3px",
        color: isDark ? "#a9b4bd" : "#35434a",
        wordBreak: "break-word",
        whiteSpace: "normal",
        lineHeight: "1.25"
    },

    // ============================================================
    // HISTORY ROW
    // ============================================================

    historyRow: {
        cursor: "pointer"
    },

    selectedHistoryRow: {
        background: isDark ? "#173049" : "#e8f3ef"
    },

    // ============================================================
    // BUTTONS
    // ============================================================

    actionArea: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "10px",
        marginTop: "-3px",
        marginBottom: "18px"
    },

    saveButton: {
        minWidth: "100px",
        height: "38px",
        background: "#1d5c4d",
        border: "1px solid #1d5c4d",
        borderRadius: "5px",
        fontWeight: "600",
        fontSize: "13px"
    },

    clearButton: {
        minWidth: "100px",
        height: "38px",
        background: "#6c757d",
        border: "1px solid #6c757d",
        borderRadius: "5px",
        fontWeight: "600",
        fontSize: "13px"
    },

    // ============================================================
    // PAGINATION
    // ============================================================

    pageButton: {
        minWidth: "90px",
        height: "34px",
        background: "#1d5c4d",
        border: "1px solid #1d5c4d",
        borderRadius: "5px",
        fontSize: "12px",
        fontWeight: "600"
    },

    pageNumber: {
        fontSize: "13px",
        fontWeight: "600",
        color: isDark ? "#c3ccd4" : "#40515a"
    },

    // ============================================================
    // LOADING
    // ============================================================

    loadingText: {
        textAlign: "center",
        padding: "20px",
        color: isDark ? "#8b96a1" : "#777",
        fontSize: "13px"
    },

    emptyText: {
        textAlign: "center",
        padding: "20px",
        color: isDark ? "#8b96a1" : "#777",
        fontSize: "13px"
    }
});

export default VendorEvaluation;

